import {
    useState,
    useMemo,
    useRef,
    type FC
} from "react";
import {
    type TextStyle,
    Pressable,
    Platform,
    View
} from "react-native";
import type IColorPickerProps from "./type";
import type {
    ColorPickerKeyPressEventType,
    ColorPickerContrastProps,
    ColorPickerFieldsProps,
    ColorPickerFormat,
    ColorPickerModel
} from "./type";
import stylesheet, {
    getColorPickerGapStyle,
    useStyles
} from "./stylesheet";
import {
    COLOR_PICKER_CONTRAST_LARGE_TARGET,
    COLOR_PICKER_AREA_ASPECT_RATIO,
    COLOR_PICKER_CONTRAST_TARGET,
    COLOR_PICKER_DEFAULT_FORMATS,
    COLOR_PICKER_DEFAULT_VALUE,
    COLOR_PICKER_FORMAT_LABELS,
    getColorPickerEyeDropper,
    COLOR_PICKER_ECHO_LIMIT,
    getColorPickerContrast,
    getColorPickerPresets,
    parseColorPickerField,
    getColorPickerFields,
    getColorPickerModel,
    getColorPickerHex,
    getColorPickerRgb
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../types";
import {
    useInteractionState,
    useStableCallback
} from "../../helpers/interaction";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    ChevronsUpDown as ChevronsUpDownIcon,
    TriangleAlert as TriangleAlertIcon,
    CircleCheck as CircleCheckIcon,
    Pipette as PipetteIcon,
    CircleX as CircleXIcon
} from "lucide-react-native";
import {
    getContrastSnapLightness,
    parseColorString,
    type ColorVector,
    formatColorHex,
    getMaxChroma,
    parseColor
} from "../../helpers/color";
import {
    getColorSwatchPickerIsMatch
} from "../colorSwatchPicker/util";
import {
    getColorSwatchSize
} from "../colorSwatch/stylesheet";
import {
    getColorSwatchLabel
} from "../colorSwatch/util";
import {
    formatNumber
} from "../../helpers/locale";
import ColorSwatchPicker from "../colorSwatchPicker";
import AnimatedIcon from "../animatedIcon";
import ColorSlider from "../colorSlider";
import ColorSwatch from "../colorSwatch";
import ColorArea from "../colorArea";
import Separator from "../separator";
import TextInput from "../textInput";
import Button from "../button";
import Badge from "../badge";
import Text from "../text";

const ColorPickerFormatIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <AnimatedIcon
    customColor={customColor}
    icon={ChevronsUpDownIcon}
    color={color}
    size={size}
/>;

const ColorPickerEyeDropperIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <AnimatedIcon
    customColor={customColor}
    animationTrigger="hover"
    animation="signature"
    icon={PipetteIcon}
    color={color}
    size={size}
/>;

const ColorPickerGoodIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <AnimatedIcon
    customColor={customColor}
    icon={CircleCheckIcon}
    color={color}
    size={size}
/>;

const ColorPickerLargeIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <AnimatedIcon
    customColor={customColor}
    icon={TriangleAlertIcon}
    color={color}
    size={size}
/>;

const ColorPickerPoorIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <AnimatedIcon
    customColor={customColor}
    icon={CircleXIcon}
    color={color}
    size={size}
/>;

const ColorPickerHashIcon: NCoreUIKitIcon = ({
    customColor
}) => {
    return <Text
        customColor={customColor}
        variant="monoMediumSize"
    >
        {"#"}
    </Text>;
};

const ColorPickerFields: FC<ColorPickerFieldsProps> = ({
    customLocalize,
    isAlphaEnabled,
    customTheme,
    isDisabled,
    onCommit,
    trailing,
    example,
    leading,
    format,
    model,
    rgb
}) => {
    const {
        typography,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const [
        drafts,
        setDrafts
    ] = useState<Record<string, string | undefined>>({
    });

    const [
        invalidKey,
        setInvalidKey
    ] = useState<string | null>(null);

    const fields = getColorPickerFields({
        isAlphaEnabled,
        activeLocale,
        format,
        model,
        rgb
    });

    const discardDraft = (key: string) => {
        setDrafts((previous) => ({
            ...previous,
            [key]: undefined
        }));

        setInvalidKey(null);
    };

    const handleCommit = (key: string) => () => {
        const text = drafts[key];

        const field = fields.find((entry) => entry.key === key);

        if(text === undefined || !field) {
            return;
        }

        const next = parseColorPickerField({
            isAlphaEnabled,
            key: field.key,
            model,
            text,
            rgb
        });

        if(!next) {
            setInvalidKey(key);

            return;
        }

        discardDraft(key);

        onCommit(next);
    };

    const handleChangeText = (key: string) => (text: string) => {
        setDrafts((previous) => ({
            ...previous,
            [key]: text
        }));
    };

    const handleKeyPress = (key: string) => (event: ColorPickerKeyPressEventType) => {
        if(event.nativeEvent.key === "Escape") {
            discardDraft(key);
        }
    };

    const inputStyle = {
        ...typography.monoMediumSize,
        fontFamily: Platform.OS === "web" ? `"${typography.monoMediumSize.fontFamily}", ui-monospace, monospace` : typography.monoMediumSize.fontFamily
    } as TextStyle;

    const isSingleRow = format === "hex" || format === "rgb" && fields.length === 3;

    const rowStyle = getColorPickerGapStyle(spaces.spacingXs);

    const inputs = fields.map((field) => <TextInput
        contentContainerStyle={{
            paddingHorizontal: spaces.spacingSm
        }}
        {...field.key === "hex" ? {
            customIconColor: colors.content.text.low,
            icon: ColorPickerHashIcon
        } : {
        }}
        type={invalidKey === field.key ? "danger" : "neutral"}
        onChangeText={handleChangeText(field.key)}
        accessibilityLabel={localize(field.label)}
        onSubmitEditing={handleCommit(field.key)}
        value={drafts[field.key] ?? field.text}
        onKeyPress={handleKeyPress(field.key)}
        aria-label={localize(field.label)}
        onBlur={handleCommit(field.key)}
        customLocalize={customLocalize}
        customTheme={customTheme}
        isDisabled={isDisabled}
        inputStyle={inputStyle}
        isClearEnabled={false}
        autoCapitalize="none"
        style={{
            flex: field.flex
        }}
        autoCorrect={false}
        key={field.key}
        size="small"
    />);

    const hint = invalidKey !== null && <Text
        customColor={colors.content.text.danger}
        customLocalize={customLocalize}
        customTheme={customTheme}
        variant="bodySmallSize"
        role="alert"
    >
        {localize("colorPicker-invalid", [
            example
        ])}
    </Text>;

    if(isSingleRow) {
        return <>
            <View
                style={[
                    stylesheet.fieldsRow,
                    rowStyle
                ]}
            >
                {leading}
                <View
                    style={[
                        stylesheet.fieldsRow,
                        stylesheet.fieldsInputs,
                        rowStyle
                    ]}
                >
                    {inputs}
                </View>
                {trailing}
            </View>
            {hint}
        </>;
    }

    return <>
        <View
            style={[
                stylesheet.formatRow,
                rowStyle
            ]}
        >
            {leading}
            {trailing}
        </View>
        <View
            style={[
                stylesheet.fieldsRow,
                rowStyle
            ]}
        >
            {inputs}
        </View>
        {hint}
    </>;
};

const ColorPickerContrast: FC<ColorPickerContrastProps> = ({
    isSnapUnreachable,
    customLocalize,
    customTheme,
    isSnapShown,
    isDisabled,
    contrast,
    onSnap
}) => {
    const {
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const contrastContainer = getColorPickerGapStyle(spaces.spacingXs);

    const contrastRow = getColorPickerGapStyle(spaces.spacingSm);

    const ratioText = `${formatNumber({
        options: {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        },
        locale: activeLocale,
        value: contrast.ratio
    })}:1`;

    const levelText = `Lc ${Math.round(contrast.level)}${contrast.isDark ? ` ${localize("colorPicker-apcaInfo")}` : ""}`;

    const status = contrast.isGood ? {
        title: localize("colorPicker-contrastGood"),
        icon: ColorPickerGoodIcon,
        type: "success" as const
    } : contrast.ratio >= COLOR_PICKER_CONTRAST_LARGE_TARGET ? {
        title: localize("colorPicker-contrastLarge"),
        icon: ColorPickerLargeIcon,
        type: "warning" as const
    } : {
        title: localize("colorPicker-contrastPoor"),
        icon: ColorPickerPoorIcon,
        type: "danger" as const
    };

    return <View
        style={[
            stylesheet.contrastContainer,
            contrastContainer
        ]}
    >
        <View
            style={[
                stylesheet.contrastRow,
                contrastRow
            ]}
        >
            <Text
                customColor={colors.content.text.low}
                customLocalize={customLocalize}
                customTheme={customTheme}
                variant="bodySmallSize"
            >
                {localize("colorPicker-contrastAgainst")}
            </Text>
            <Text
                customColor={colors.content.text.high}
                customLocalize={customLocalize}
                customTheme={customTheme}
                variant="labelMediumSize"
                style={stylesheet.ratio}
            >
                {ratioText}
            </Text>
            <Text
                customColor={colors.content.text.mid}
                customLocalize={customLocalize}
                customTheme={customTheme}
                variant="bodySmallSize"
            >
                {levelText}
            </Text>
        </View>
        <View
            style={[
                stylesheet.contrastRow,
                contrastRow
            ]}
        >
            <Badge
                customTheme={customTheme}
                title={status.title}
                icon={status.icon}
                type={status.type}
                variant="tonal"
                size="small"
            />
            {
                isSnapShown && <Button
                    title={localize("colorPicker-contrastSnap")}
                    customTheme={customTheme}
                    isDisabled={isDisabled}
                    onPress={onSnap}
                    variant="ghost"
                    size="small"
                />
            }
            {
                isSnapUnreachable && <Text
                    customColor={colors.content.text.disabled}
                    customLocalize={customLocalize}
                    customTheme={customTheme}
                    variant="bodySmallSize"
                >
                    {localize("colorPicker-contrastUnreachable")}
                </Text>
            }
        </View>
    </View>;
};

const ColorPicker: FC<IColorPickerProps> = ({
    initialValue = COLOR_PICKER_DEFAULT_VALUE,
    formats = COLOR_PICKER_DEFAULT_FORMATS,
    spreadBehaviour = "baseline",
    "aria-label": ariaLabelProp,
    isEyeDropperEnabled = true,
    isAlphaEnabled = false,
    isContrastShown = true,
    initialFormat = "hex",
    areaShape = "square",
    presets: presetsProp,
    isAreaFlush = false,
    isDisabled = false,
    value: valueProp,
    contrastSurface,
    customLocalize,
    recentColors,
    customTheme,
    onChangeEnd,
    onChange,
    style,
    ...props
}) => {
    const {
        controlHeights,
        shapeConfigs,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const swatchSize = pointerType === "coarse" ? "large" : "small";

    const {
        interactionHandlers,
        isFocusVisible
    } = useInteractionState({
        isDisabled
    });

    const [
        innerModel,
        setInnerModel
    ] = useState<ColorPickerModel>(() => getColorPickerModel({
        value: valueProp ?? initialValue,
        isAlphaEnabled,
        hue: 0
    }) ?? {
        rgb: null,
        alpha: 1,
        c: 0,
        h: 0,
        l: 0
    });

    const [
        previousModel
    ] = useState<ColorPickerModel>(innerModel);

    const [
        syncedValue,
        setSyncedValue
    ] = useState<string | undefined>(valueProp);

    const [
        format,
        setFormat
    ] = useState<ColorPickerFormat>(formats.includes(initialFormat) ? initialFormat : formats[0] ?? "hex");

    const modelRef = useRef<ColorPickerModel>(innerModel);

    const emittedRef = useRef<Array<string>>([]);

    if(valueProp !== syncedValue) {
        setSyncedValue(valueProp);

        const valueModel = valueProp !== undefined ? getColorPickerModel({
            hue: innerModel.h,
            value: valueProp,
            isAlphaEnabled
        }) : null;

        const valueHex = valueModel ? getColorPickerHex({
            model: valueModel,
            isAlphaEnabled
        }) : null;

        const emitted = emittedRef.current;

        const isEcho = valueHex !== null && emitted.includes(valueHex);

        if(isEcho && valueHex === emitted[emitted.length - 1]) {
            emittedRef.current = [];
        }

        if(valueModel && !isEcho && valueHex !== getColorPickerHex({
            model: innerModel,
            isAlphaEnabled
        })) {
            emittedRef.current = [];

            setInnerModel(valueModel);
        }
    }

    const model = innerModel;

    modelRef.current = model;

    const rgb = getColorPickerRgb(model);

    const hex = getColorPickerHex({
        isAlphaEnabled,
        model
    });

    const opaqueHex = formatColorHex({
        isAlphaEnabled: false,
        alpha: 1,
        rgb
    });

    const surfaceColor = contrastSurface ?? colors.content.container.default;

    const surface = useMemo<ColorVector>(() => parseColor(surfaceColor)?.rgb ?? [
        1,
        1,
        1
    ], [
        surfaceColor
    ]);

    const contrast = useMemo(() => getColorPickerContrast({
        surface,
        model
    }), [
        surface,
        model
    ]);

    const snapLightness = useMemo(() => contrast.isGood ? null : getContrastSnapLightness({
        target: COLOR_PICKER_CONTRAST_TARGET,
        isApcaGated: !contrast.isDark,
        alpha: model.alpha,
        c: model.c,
        h: model.h,
        l: model.l,
        surface
    }), [
        contrast,
        surface,
        model
    ]);

    const presets = useMemo(() => presetsProp ?? getColorPickerPresets({
        localize,
        colors
    }), [
        presetsProp,
        localize,
        colors
    ]);

    const recentItems = useMemo(() => (recentColors ?? []).map((entry) => {
        if(typeof entry !== "string") {
            return entry;
        }

        const parsed = parseColorString(entry);

        return {
            title: parsed ? getColorSwatchLabel({
                color: parsed,
                localize
            }) : entry,
            color: entry
        };
    }), [
        recentColors,
        localize
    ]);

    const previewSize = getColorSwatchSize({
        controlHeights,
        size: "large",
        iconSizes,
        spaces
    }).size;

    const isEyeDropperShown = Platform.OS === "web" && isEyeDropperEnabled && getColorPickerEyeDropper() !== undefined;

    const {
        previewCurrentSwatch,
        separatorContainer,
        contentContainer,
        areaContainer,
        sliderColumn,
        presetGroup,
        previewHalf,
        container,
        preview,
        topRow
    } = useStyles({
        width: shapeConfigs.drawerWidth + spaces.spacingMd,
        spreadBehaviour,
        isFocusVisible,
        shapeConfigs,
        isAreaFlush,
        previewSize,
        radiuses,
        borders,
        colors,
        spaces
    });

    const update = (next: ColorPickerModel) => {
        const nextHex = getColorPickerHex({
            isAlphaEnabled,
            model: next
        });

        modelRef.current = next;

        emittedRef.current = [
            ...emittedRef.current.slice(1 - COLOR_PICKER_ECHO_LIMIT),
            nextHex
        ];

        setInnerModel(next);

        onChange?.(nextHex);
    };

    const finish = () => {
        onChangeEnd?.(getColorPickerHex({
            model: modelRef.current,
            isAlphaEnabled
        }));
    };

    const commit = (next: ColorPickerModel) => {
        update(next);

        finish();
    };

    const handleAreaChange = (value: {
        lightness: number;
        chroma: number;
    }) => {
        update({
            ...modelRef.current,
            l: value.lightness,
            c: value.chroma,
            rgb: null
        });
    };

    const handleHueChange = (value: number) => {
        const current = modelRef.current;

        const maxChroma = getMaxChroma(current.l, current.h);

        update({
            ...current,
            c: areaShape === "square" && maxChroma > 0 ? Math.min(current.c / maxChroma, 1) * getMaxChroma(current.l, value) : current.c,
            rgb: null,
            h: value
        });
    };

    const handleAlphaChange = (value: number) => {
        update({
            ...modelRef.current,
            alpha: value
        });
    };

    const handleRestore = () => {
        commit(previousModel);
    };

    const handleFormatPress = () => {
        const index = formats.indexOf(format);

        setFormat(formats[(index + 1) % formats.length] ?? format);
    };

    const handleSnapPress = () => {
        if(snapLightness === null) {
            return;
        }

        commit({
            ...modelRef.current,
            l: snapLightness,
            rgb: null
        });
    };

    const handleEyeDropperPress = () => {
        const EyeDropper = getColorPickerEyeDropper();

        if(!EyeDropper) {
            return;
        }

        new EyeDropper().open().then((result) => {
            const picked = getColorPickerModel({
                hue: modelRef.current.h,
                value: result.sRGBHex,
                isAlphaEnabled
            });

            if(picked) {
                commit({
                    ...picked,
                    alpha: modelRef.current.alpha
                });
            }
        }).catch(() => undefined);
    };

    const handleSwatchChange = useStableCallback((value: string) => {
        const picked = getColorPickerModel({
            hue: modelRef.current.h,
            isAlphaEnabled,
            value
        });

        if(!picked) {
            return;
        }

        commit({
            ...picked,
            alpha: picked.alpha === 1 ? modelRef.current.alpha : picked.alpha
        });
    });

    const presetValue = presets.some((item) => getColorSwatchPickerIsMatch({
        color: item.color,
        value: hex
    })) ? hex : "";

    const recentValue = recentItems.some((item) => getColorSwatchPickerIsMatch({
        color: item.color,
        value: hex
    })) ? hex : "";

    const formatButton = <Button
        accessibilityLabel={localize("colorPicker-format", [
            COLOR_PICKER_FORMAT_LABELS[format]
        ])}
        aria-label={localize("colorPicker-format", [
            COLOR_PICKER_FORMAT_LABELS[format]
        ])}
        title={COLOR_PICKER_FORMAT_LABELS[format]}
        icon={ColorPickerFormatIcon}
        onPress={handleFormatPress}
        customTheme={customTheme}
        isDisabled={isDisabled}
        iconDirection="end"
        variant="ghost"
        type="neutral"
        size="small"
    />;

    const eyeDropperButton = isEyeDropperShown && <Button
        accessibilityLabel={localize("colorPicker-eyeDropper")}
        aria-label={localize("colorPicker-eyeDropper")}
        icon={ColorPickerEyeDropperIcon}
        onPress={handleEyeDropperPress}
        customTheme={customTheme}
        isDisabled={isDisabled}
        variant="ghost"
        type="neutral"
        size="small"
    />;

    const label = ariaLabelProp ?? localize("colorPicker-label");

    return <View
        {...props}
        accessibilityLabel={label}
        aria-disabled={isDisabled}
        aria-label={label}
        style={[
            style,
            container
        ]}
        role="group"
    >
        <View
            style={areaContainer}
        >
            <ColorArea
                radius={isAreaFlush ? Math.max(0, radiuses.surface - borders.line) : undefined}
                aspectRatio={COLOR_PICKER_AREA_ASPECT_RATIO}
                customLocalize={customLocalize}
                contrastSurface={surfaceColor}
                onChange={handleAreaChange}
                customTheme={customTheme}
                value={{
                    lightness: model.l,
                    chroma: model.c
                }}
                isDisabled={isDisabled}
                isFlush={isAreaFlush}
                onChangeEnd={finish}
                alpha={model.alpha}
                shape={areaShape}
                hue={model.h}
            />
        </View>
        <View
            style={contentContainer}
        >
            <View
                style={[
                    stylesheet.topRow,
                    topRow
                ]}
            >
                <View
                    style={[
                        stylesheet.sliderColumn,
                        sliderColumn
                    ]}
                >
                    <ColorSlider
                        isChromaRelative={areaShape === "square"}
                        customLocalize={customLocalize}
                        onChange={handleHueChange}
                        customTheme={customTheme}
                        isDisabled={isDisabled}
                        onChangeEnd={finish}
                        color={opaqueHex}
                        value={model.h}
                        channel="hue"
                    />
                    {
                        isAlphaEnabled && <ColorSlider
                            customLocalize={customLocalize}
                            onChange={handleAlphaChange}
                            customTheme={customTheme}
                            isDisabled={isDisabled}
                            onChangeEnd={finish}
                            value={model.alpha}
                            color={opaqueHex}
                            channel="alpha"
                        />
                    }
                </View>
                <View
                    style={[
                        stylesheet.preview,
                        preview
                    ]}
                >
                    <Pressable
                        accessibilityLabel={localize("colorPicker-previous", [
                            getColorPickerHex({
                                model: previousModel,
                                isAlphaEnabled
                            })
                        ])}
                        aria-label={localize("colorPicker-previous", [
                            getColorPickerHex({
                                model: previousModel,
                                isAlphaEnabled
                            })
                        ])}
                        style={[
                            stylesheet.previewHalf,
                            previewHalf
                        ]}
                        {...interactionHandlers}
                        onPress={handleRestore}
                        disabled={isDisabled}
                        role="button"
                    >
                        <ColorSwatch
                            customLocalize={customLocalize}
                            surfaceColor={surfaceColor}
                            color={getColorPickerHex({
                                model: previousModel,
                                isAlphaEnabled: true
                            })}
                            customTheme={customTheme}
                            size="large"
                        />
                    </Pressable>
                    <View
                        accessibilityLabel={localize("colorPicker-current", [
                            hex
                        ])}
                        aria-label={localize("colorPicker-current", [
                            hex
                        ])}
                        style={[
                            stylesheet.previewHalf,
                            previewHalf
                        ]}
                        role="img"
                    >
                        <ColorSwatch
                            customLocalize={customLocalize}
                            style={previewCurrentSwatch}
                            surfaceColor={surfaceColor}
                            color={getColorPickerHex({
                                isAlphaEnabled: true,
                                model
                            })}
                            customTheme={customTheme}
                            size="large"
                        />
                    </View>
                </View>
            </View>
            <ColorPickerFields
                customLocalize={customLocalize}
                isAlphaEnabled={isAlphaEnabled}
                example={getColorPickerHex({
                    isAlphaEnabled,
                    model
                })}
                trailing={eyeDropperButton}
                customTheme={customTheme}
                isDisabled={isDisabled}
                leading={formatButton}
                onCommit={commit}
                format={format}
                model={model}
                key={format}
                rgb={rgb}
            />
            {
                isContrastShown && <ColorPickerContrast
                    isSnapUnreachable={!contrast.isGood && snapLightness === null}
                    isSnapShown={!contrast.isGood && snapLightness !== null}
                    customLocalize={customLocalize}
                    customTheme={customTheme}
                    onSnap={handleSnapPress}
                    isDisabled={isDisabled}
                    contrast={contrast}
                />
            }
            {
                (presets.length > 0 || recentItems.length > 0) && <View
                    style={[
                        stylesheet.separatorContainer,
                        separatorContainer
                    ]}
                >
                    <Separator
                        customLocalize={customLocalize}
                        customTheme={customTheme}
                    />
                </View>
            }
            {
                presets.length > 0 && <View
                    style={presetGroup}
                >
                    <Text
                        customColor={colors.content.text.low}
                        customLocalize={customLocalize}
                        customTheme={customTheme}
                        variant="labelSmallSize"
                    >
                        {localize("colorPicker-presets")}
                    </Text>
                    <ColorSwatchPicker
                        title={localize("colorPicker-presets")}
                        customLocalize={customLocalize}
                        onChange={handleSwatchChange}
                        surfaceColor={surfaceColor}
                        customTheme={customTheme}
                        columns={presets.length}
                        isDisabled={isDisabled}
                        value={presetValue}
                        size={swatchSize}
                        items={presets}
                        layout="row"
                    />
                </View>
            }
            {
                recentItems.length > 0 && <View
                    style={presetGroup}
                >
                    <Text
                        customColor={colors.content.text.low}
                        customLocalize={customLocalize}
                        customTheme={customTheme}
                        variant="labelSmallSize"
                    >
                        {localize("colorPicker-recent")}
                    </Text>
                    <ColorSwatchPicker
                        title={localize("colorPicker-recent")}
                        customLocalize={customLocalize}
                        onChange={handleSwatchChange}
                        columns={recentItems.length}
                        surfaceColor={surfaceColor}
                        customTheme={customTheme}
                        isDisabled={isDisabled}
                        value={recentValue}
                        items={recentItems}
                        size={swatchSize}
                        layout="row"
                    />
                </View>
            }
        </View>
    </View>;
};

export default ColorPicker;
